import { CARD_REGEX, CYBERSOURCE_COMPANY_NAME_ISSUE } from 'constants/AppConstants';
import keysProviderService from 'services/keysProvider.service';
import cyberSourceToken from 'services/cyberSourceToken.service';
import { getCybersourceCompanyNameIssue } from 'utils/validation.util';
import { PAYMENT_METHOD_OPTION_IDS } from './PaymentInformationConstants';

export const validCreditCardNumber = (cardNumber) => {
  if (!cardNumber) return false;
  const sanitizedCardNumber = cardNumber.replaceAll(/[\s-]/g, '');
  if (!CARD_REGEX.test(sanitizedCardNumber)) {
    return false;
  }
  // #genai Card-number correctness is validated server-side via the Flex tokenize call;
  // this local check only enforces the regex-level format so the form can submit.
  return true;
};

export const validExpiryDate = (dateInput) => {
  const parts = dateInput.split('/');
  if (parts.length !== 2) {
    return false;
  }
  const date = new Date(dateInput.split('/')[1], dateInput.split('/')[0], 1);

  const currentDate = new Date();
  if (date < currentDate) {
    return false;
  }
  return true;
};

export const paymentFields = {
  paymentMethod: 'paymentMethod',
  firstName: 'firstName',
  lastName: 'lastName',
  cardNumber: 'cardNumber',
  cardType: 'cardType',
  expirationDateMonth: 'expirationDateMonth',
  expirationDateYear: 'expirationDateYear',
  accountType: 'accountType',
  routingNumber: 'routingNumber',
  accountNumber: 'accountNumber',
  accountNumberConfirm: 'accountNumberConfirm',
};

// #genai Phase 2 First / Last name reuse the Cybersource company rules (trim, first character,
// charset, PII). Inline names the failed rule; the banner stays one short sentence. Required
// copy stays the existing field-specific banner + generic inline keys.
const PHASE2_PERSON_NAME_COPY = {
  [paymentFields.firstName]: {
    requiredBanner: 'payment_information__first_name_required',
    invalidBanner: 'payment_information__first_name_invalid',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.PII]: 'payment_information__first_name_pii',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.FIRST_CHAR]: 'payment_information__first_name_first_char',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.CHARSET]: 'payment_information__first_name_unsupported',
  },
  [paymentFields.lastName]: {
    requiredBanner: 'payment_information__last_name_required',
    invalidBanner: 'payment_information__last_name_invalid',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.PII]: 'payment_information__last_name_pii',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.FIRST_CHAR]: 'payment_information__last_name_first_char',
    [CYBERSOURCE_COMPANY_NAME_ISSUE.CHARSET]: 'payment_information__last_name_unsupported',
  },
};

const getPhase2PersonNameErrors = (field, value, t, requiredInlineKey) => {
  const copy = PHASE2_PERSON_NAME_COPY[field];
  const issue = getCybersourceCompanyNameIssue(value);
  if (!copy || issue === CYBERSOURCE_COMPANY_NAME_ISSUE.VALID) {
    return { inline: '', banner: '' };
  }
  if (issue === CYBERSOURCE_COMPANY_NAME_ISSUE.EMPTY) {
    return { inline: t(requiredInlineKey), banner: t(copy.requiredBanner) };
  }
  return { inline: t(copy[issue]), banner: t(copy.invalidBanner) };
};

// #genai undefined = this field is not a Phase 2 name check (caller keeps its existing rules).
// A string (including '') is the resolved inline or banner message.
const phase2PersonNameMessage = (field, value, t, options, requiredInlineKey, part) => {
  if (!options?.validateCybersourceNames || !PHASE2_PERSON_NAME_COPY[field]) return undefined;
  return getPhase2PersonNameErrors(field, value, t, requiredInlineKey)[part];
};

const phase2NameOptionsFromToken = (transientTokenData) => ({
  validateCybersourceNames: Boolean(transientTokenData?.isPhase2Ach || transientTokenData?.isPhase2Card),
});

const validateCardValue = (field, inputValues, t, options) => {
  const phase2Name = phase2PersonNameMessage(
    field,
    inputValues[field],
    t,
    options,
    'payment_information__card_fields_error',
    'inline',
  );
  if (phase2Name !== undefined) return phase2Name;
  switch (field) {
    case paymentFields.firstName:
    case paymentFields.lastName:
    case paymentFields.cardType:
    case paymentFields.expirationDateYear: {
      if (!inputValues[field]) return t('payment_information__card_fields_error');
      return '';
    }
    case paymentFields.cardNumber: {
      return '';
    }
    case paymentFields.expirationDateMonth: {
      if (!inputValues[field]) return t('payment_information__card_fields_error');
      if (inputValues[paymentFields.expirationDateYear] && !validExpiryDate(`${inputValues[field]}/
        ${inputValues[paymentFields.expirationDateYear]}`)) {
        return t('payment_information__expiration_date_error');
      }
      return '';
    }
    default: return '';
  }
};

// #genai Inline (under-field) blank-required copy is generic; banner keeps field-specific names
// via validateAchValueForBanner (Phase 2 Add ACH / Add Card UX feedback).
const validateAchValue = (field, inputValues, t, options) => {
  const phase2Name = phase2PersonNameMessage(
    field,
    inputValues[field],
    t,
    options,
    'payment_information__ach_fields_error',
    'inline',
  );
  if (phase2Name !== undefined) return phase2Name;
  if (inputValues[field]) return '';
  switch (field) {
    case paymentFields.firstName:
    case paymentFields.lastName:
    case paymentFields.accountType:
    case paymentFields.routingNumber:
    case paymentFields.accountNumber:
    case paymentFields.accountNumberConfirm:
      return t('payment_information__ach_fields_error');
    default:
      return '';
  }
};

export const validateValue = (field, inputValues, t, options) => (
  inputValues[paymentFields.paymentMethod] === PAYMENT_METHOD_OPTION_IDS.Ach
    ? validateAchValue(field, inputValues, t, options)
    : validateCardValue(field, inputValues, t, options)
);

// #genai ACH fields validated on Save. First / Last name are always required. On Phase 2 they
// also use the Cybersource company rules (trim, first character, charset, PII): the inline
// helper names the failed rule and the banner stays one short sentence.
const getAchFieldsToValidate = () => [
  paymentFields.firstName,
  paymentFields.lastName,
  paymentFields.accountType,
  paymentFields.routingNumber,
  paymentFields.accountNumber,
  paymentFields.accountNumberConfirm,
];

const validateCardValueForBanner = (field, inputValues, t, options) => {
  const phase2Name = phase2PersonNameMessage(
    field,
    inputValues[field],
    t,
    options,
    'payment_information__card_fields_error',
    'banner',
  );
  if (phase2Name !== undefined) return phase2Name;
  switch (field) {
    case paymentFields.firstName:
      if (!inputValues[field]) return t('payment_information__first_name_required');
      return '';
    case paymentFields.lastName:
      if (!inputValues[field]) return t('payment_information__last_name_required');
      return '';
    case paymentFields.cardType:
      if (!inputValues[field]) return t('payment_information__card_type_required');
      return '';
    case paymentFields.expirationDateYear: {
      if (!inputValues[field]) return t('payment_information__expiration_year_required');
      return '';
    }
    case paymentFields.cardNumber: {
      // Skip card number validation - handled by microform/payment processor
      return '';
    }
    case paymentFields.expirationDateMonth: {
      if (!inputValues[field]) return t('payment_information__expiration_month_required');
      if (inputValues[paymentFields.expirationDateYear] && !validExpiryDate(`${inputValues[field]}/
        ${inputValues[paymentFields.expirationDateYear]}`)) {
        return t('payment_information__expiration_date_error');
      }
      return '';
    }
    default: return '';
  }
};

const validateAchValueForBanner = (field, inputValues, t, options) => {
  const phase2Name = phase2PersonNameMessage(
    field,
    inputValues[field],
    t,
    options,
    'payment_information__ach_fields_error',
    'banner',
  );
  if (phase2Name !== undefined) return phase2Name;
  switch (field) {
    case paymentFields.firstName:
      if (!inputValues[field]) return t('payment_information__first_name_required');
      return '';
    case paymentFields.lastName:
      if (!inputValues[field]) return t('payment_information__last_name_required');
      return '';
    case paymentFields.accountType:
      if (!inputValues[field]) return t('payment_information__account_type_required');
      return '';
    case paymentFields.routingNumber:
      if (!inputValues[field]) return t('payment_information__routing_number_required');
      return '';
    case paymentFields.accountNumber:
      if (!inputValues[field]) return t('payment_information__account_number_required');
      return '';
    case paymentFields.accountNumberConfirm:
      if (!inputValues[field]) return t('payment_information__account_number_confirm_required');
      return '';
    default:
      return '';
  }
};

export const validateValueForBanner = (field, inputValues, t, options) => (
  inputValues[paymentFields.paymentMethod] === PAYMENT_METHOD_OPTION_IDS.Ach
    ? validateAchValueForBanner(field, inputValues, t, options)
    : validateCardValueForBanner(field, inputValues, t, options)
);

// #genai Shared per-field error mapping for ACH token failures. Used by both the inline
// validator and the banner builder so the two outputs stay aligned.
// Returns a partial overrides object; callers merge it into errorMsgs with Object.assign —
// avoids mutating a function parameter directly (no-param-reassign).
function buildAchTokenFailureOverrides(existingAccountNumberMsg, transientTokenData, t) {
  const overrides = {};

  if (transientTokenData.isRoutingNumberInvalid) {
    overrides.routingNumber = t('payment_information__routing_number_invalid');
  }
  if (transientTokenData.isAccountNumberMismatch) {
    overrides.accountNumber = t('payment_information__ach_account_numbers_dont_match');
  } else if (transientTokenData.isAccountNumberInvalid) {
    overrides.accountNumber = t('payment_information__account_number_invalid');
  }
  // Confirm gets no override: mismatch is anchored on accountNumber (banner de-dup) and an
  // empty confirm already carries "is required" from validateAchValue.

  // #genai Safety net: tokenization failed but CyberSource didn't give us a per-field detail
  // we recognize (network blip, expired capture context, new error shape, etc). Without this,
  // the user is silently blocked — Save does nothing and no banner appears. Anchor the
  // generic message on accountNumber so the banner shows at least one entry.
  const hasFieldOverride = transientTokenData.isRoutingNumberInvalid
    || transientTokenData.isAccountNumberMismatch
    || transientTokenData.isAccountNumberInvalid;
  if (!hasFieldOverride && !overrides.accountNumber && !existingAccountNumberMsg) {
    overrides.accountNumber = t('payment_information__ach_token_validation');
  }

  return overrides;
}

export const validatePaymentInfo = (inputValues, t, transientTokenData = {}) => {
  const errorMsgs = {
    paymentMethod: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    cardType: '',
    expirationDateMonth: '',
    expirationDateYear: '',
    accountType: '',
    routingNumber: '',
    accountNumber: '',
    accountNumberConfirm: '',
    isError: false,
  };
  const paymentMethod = inputValues[paymentFields.paymentMethod];
  // #genai - ACH uses Flex Microform hosted fields; validate our fields + hosted field presence
  const fieldsToValidate = paymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach
    ? getAchFieldsToValidate()
    : [
      paymentFields.firstName,
      paymentFields.lastName,
      paymentFields.cardType,
      paymentFields.cardNumber,
      paymentFields.expirationDateMonth,
      paymentFields.expirationDateYear,
    ];

  const phase2NameOptions = phase2NameOptionsFromToken(transientTokenData);
  fieldsToValidate.forEach((field) => {
    const errorMsg = validateValue(field, inputValues, t, phase2NameOptions);
    if (errorMsg !== '') {
      errorMsgs.isError = true;
    }
    errorMsgs[field] = errorMsg;
  });

  if (paymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach) {
    if (transientTokenData.isAchUpdated && !transientTokenData.transientToken) {
      // #genai Strict per-field mapping based on (1) the field's sentinel state and (2) the
      // per-field flags CyberSource gave us via handleTokenisation. The base validateAchValue
      // pass above already set empty → "is required" / filled → '' for each field; here we
      // just upgrade "filled-but-rejected" into the specific "invalid" / "do not match"
      // message. Valid fields stay clean.
      Object.assign(
        errorMsgs,
        buildAchTokenFailureOverrides(errorMsgs.accountNumber, transientTokenData, t),
      );
      errorMsgs.isError = true; // eslint-disable-line no-param-reassign
    }
  } else if (transientTokenData.isCardNumberUpdated && !transientTokenData.transientToken) {
    errorMsgs.cardNumber = t('payment_information__card_validation'); // eslint-disable-line no-param-reassign
    errorMsgs.isError = true; // eslint-disable-line no-param-reassign
  }
  return errorMsgs;
};

export const validatePaymentInfoForBanner = (inputValues, t, transientTokenData = {}) => {
  const errorMsgs = {
    paymentMethod: '',
    firstName: '',
    lastName: '',
    cardType: '',
    cardNumber: '',
    expirationDateMonth: '',
    expirationDateYear: '',
    accountType: '',
    routingNumber: '',
    accountNumber: '',
    accountNumberConfirm: '',
    isError: false,
  };
  const paymentMethod = inputValues[paymentFields.paymentMethod];
  // #genai - ACH uses Flex Microform hosted fields; validate our fields + hosted field presence
  const fieldsToValidate = paymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach
    ? getAchFieldsToValidate()
    : [
      paymentFields.firstName,
      paymentFields.lastName,
      paymentFields.cardType,
      paymentFields.cardNumber,
      paymentFields.expirationDateMonth,
      paymentFields.expirationDateYear,
    ];

  const phase2NameOptions = phase2NameOptionsFromToken(transientTokenData);
  fieldsToValidate.forEach((field) => {
    const errorMsg = validateValueForBanner(field, inputValues, t, phase2NameOptions);
    if (errorMsg !== '') {
      errorMsgs.isError = true;
    }
    errorMsgs[field] = errorMsg;
  });

  if (paymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach) {
    if (transientTokenData.isAchUpdated && !transientTokenData.transientToken) {
      // #genai Banner mirrors the inline mapping so the validation summary at the top of the
      // page lists exactly the same per-field problems the user sees inline.
      Object.assign(
        errorMsgs,
        buildAchTokenFailureOverrides(errorMsgs.accountNumber, transientTokenData, t),
      );
      errorMsgs.isError = true; // eslint-disable-line no-param-reassign
    }
  } else if (transientTokenData.isCardNumberUpdated && !transientTokenData.transientToken) {
    errorMsgs.cardNumber = t('payment_information__card_validation'); // eslint-disable-line no-param-reassign
    errorMsgs.isError = true; // eslint-disable-line no-param-reassign
  }
  return errorMsgs;
};

export const getPaymentTokenInfo = async (
  cardNumber,
  cardType,
  expirationMonth,
  expirationYear,
) => {
  const response = await keysProviderService();
  if (!response.ok) {
    const error = new Error(response.json.errorDescription);
    error.errorCode = response.json.errorCode;
    throw error;
  }
  const keysResponse = response.json;
  const tokenResponse = await cyberSourceToken(cardNumber, cardType, keysResponse, expirationMonth, expirationYear);
  if (!tokenResponse.ok) {
    const { json: { error: errorMessage } } = tokenResponse;
    const error = new Error(errorMessage);
    error.errorCode = 0;
    throw error;
  }
  const { json: { token, maskedPan, _embedded: { icsReply: { instrumentIdentifier: { id } } } } } = tokenResponse;
  return { token, maskedPan, instrumentIdentifierId: id };
};

export const decodeCaptureContext = (captureContext) => {
  const parts = captureContext.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }
  const payload = JSON.parse(atob(parts[1]));
  return payload;
};

export const validateMicroformState = (microform, cardNumber) => {
  if (!microform) {
    throw new Error('Payment form not initialized');
  }

  if (!cardNumber) {
    throw new Error('Card fields not initialized');
  }

  return cardNumber;
};

export const getTokenOptions = (paymentInfo) => {
  const formattedMonth = String(paymentInfo.expirationDateMonth).padStart(2, '0');
  return {
    expirationMonth: formattedMonth,
    expirationYear: paymentInfo.expirationDateYear,
  };
};

export const getAchTokenOptions = (paymentInfo) => ({
  // #genai - The check microform keeps account type outside the hosted fields.
  accountType: paymentInfo.accountType,
});
